import * as XLSX from 'xlsx';
import { ReportData } from './types';

function fmt(n: number, decimals = 0): number {
  return Math.round(n * 10 ** decimals) / 10 ** decimals;
}

function fmtPct(n: number): string {
  return `${fmt(n, 1)}%`;
}

function buildProjectSummarySheet(data: ReportData): XLSX.WorkSheet {
  const { project, heroMetrics } = data;
  const rows: (string | number | undefined)[][] = [
    ['Project Name', project.projectName],
    ['Technology', project.techLabel],
    ['Size (MW)', project.capacityMw],
    ['Generation (GWh)', fmt(heroMetrics.generationMwh / 1000, 3)],
    ['Capacity Factor', project.capacityFactor != null ? fmt(project.capacityFactor, 4) : undefined],
    ['Years of Life Saved (DALYs)', fmt(heroMetrics.dalys, 1)],
    ['Species Saved (PDFs)', fmt(heroMetrics.pdfs, 1)],
    ['Economic Benefit ($USD)', fmt(heroMetrics.usd)],
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildGenerationSheet(data: ReportData): XLSX.WorkSheet {
  // Historical = generation in the project's own balancing authority.
  // Difference = TOTAL displacement (local BA + interconnection).
  // Interconnection = the portion of that displacement in neighboring BAs
  // (negative = displaced, matching Difference; 0 when no cross-border data).
  const rows: (string | number)[][] = [
    ['Fuel Type', 'Historical (GWh)', 'Difference (GWh)', 'Interconnection (GWh)'],
  ];
  for (const entry of data.generation) {
    const historical = typeof entry.values['Historical'] === 'number'
      ? (entry.values['Historical'] as number)
      : 0;
    // The model stores generation difference under the "Difference" key (GWh).
    // Positive = increased generation (e.g. Solar), negative = displaced (e.g. Coal, Gas).
    const gwh = typeof entry.values['Difference'] === 'number'
      ? entry.values['Difference']
      : typeof entry.values['GWh'] === 'number'
        ? entry.values['GWh']
        : typeof entry.values['MWh'] === 'number'
          ? (entry.values['MWh'] as number) / 1000
          : Object.values(entry.values).find(v => typeof v === 'number') as number ?? 0;
    const interconnection = typeof entry.values['Interconnection'] === 'number'
      ? (entry.values['Interconnection'] as number)
      : 0;
    rows.push([entry.fuelType, fmt(historical), fmt(gwh), fmt(interconnection)]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildEmissionsSheet(entries: ReportData['emissions']['air']): XLSX.WorkSheet {
  const rows: (string | number)[][] = [['Emission Type', 'Value', 'Unit']];
  for (const e of entries) {
    const unit = (e.Unit as string) || e.unit || '';
    rows.push([e.name, fmt(e.value, 2), unit]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildHealthBreakdownSheet(data: ReportData): XLSX.WorkSheet {
  const rows: (string | number)[][] = [['Impact Category', 'DALYs', 'USD', 'Percentage']];
  for (const e of data.impactBreakdown.health) {
    rows.push([e.category, fmt(e.value, 4), fmt(e.usd), fmtPct(e.percentage ?? 0)]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildBiodiversityBreakdownSheet(data: ReportData): XLSX.WorkSheet {
  const rows: (string | number)[][] = [['Impact Category', 'PDFs', 'USD', 'Percentage']];
  for (const e of data.impactBreakdown.biodiversity) {
    rows.push([e.category, fmt(e.value, 6), fmt(e.usd), fmtPct(e.percentage ?? 0)]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildEconomicBreakdownSheet(data: ReportData): XLSX.WorkSheet {
  const rows: (string | number)[][] = [['Impact Category', 'USD', 'Percentage']];
  for (const e of data.impactBreakdown.economic) {
    rows.push([e.category, fmt(e.usd), fmtPct(e.percentage ?? 0)]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

export function buildExcelWorkbook(data: ReportData): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildProjectSummarySheet(data), 'Project Impact Summary');
  XLSX.utils.book_append_sheet(wb, buildGenerationSheet(data), 'Generation Impact');
  XLSX.utils.book_append_sheet(wb, buildEmissionsSheet(data.emissions.air), 'Avoided Air Emissions');
  XLSX.utils.book_append_sheet(wb, buildEmissionsSheet(data.emissions.water), 'Avoided Water Emissions');
  XLSX.utils.book_append_sheet(wb, buildEmissionsSheet(data.emissions.soil), 'Avoided Soil Emissions');
  XLSX.utils.book_append_sheet(wb, buildEmissionsSheet(data.emissions.resources), 'Avoided Resource Use');
  XLSX.utils.book_append_sheet(wb, buildHealthBreakdownSheet(data), 'Health Impact Breakdown');
  XLSX.utils.book_append_sheet(wb, buildBiodiversityBreakdownSheet(data), 'Biodiversity Breakdown');
  XLSX.utils.book_append_sheet(wb, buildEconomicBreakdownSheet(data), 'Economic Breakdown');

  return wb;
}

export function downloadWorkbook(wb: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(wb, filename);
}
